import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtNum } from "../../api";
import { useAuth } from "../../AuthContext";
import { SectionTitle, StatCard, EmptyState } from "../../components/Atoms";
import { CategoryPie } from "../../components/Charts";
import { Users, Stack, ListChecks, ChartBar, ArrowRight, Funnel } from "@phosphor-icons/react";

const TIME_OPTS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];
const GENDER_BUCKETS = ["Male", "Female", "Prefer not to say"];

const COLORS = ["#22D3EE", "#EC4899", "#A3E635", "#F59E0B", "#8B5CF6", "#10B981", "#FB7185", "#F97316"];

const StatsSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
    {[0,1,2,3,4,5].map(i => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)}
  </div>
);

export default function ClientDashboard() {
  const { user } = useAuth();
  // Independent state buckets so each section paints as soon as its data is ready
  const [projects, setProjects] = useState(null);   // null = loading, [] = empty
  const [subs, setSubs]         = useState(null);
  const [creators, setCreators] = useState(null);

  // 1) Projects first (smallest payload, drives stat cards + filter dropdown)
  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data)).catch(() => setProjects([]));
  }, []);
  // 2) Approved-applications + ambassadors (in parallel) → demographics + creator-count
  useEffect(() => {
    Promise.all([
      api.get("/applications?status=Approved"),
      api.get("/ambassadors"),
    ]).then(([apps, ambs]) => {
      const ids = new Set(apps.data.map(a => a.ambassador_id));
      setCreators(ambs.data.filter(a => ids.has(a.id)));
    }).catch(() => setCreators([]));
  }, []);
  // 3) Submissions last (heaviest payload)
  useEffect(() => {
    api.get("/submissions").then(r => setSubs(r.data)).catch(() => setSubs([]));
  }, []);

  const [filters, setFilters] = useState({ project: "all", time: "all" });

  const projectsList = projects || [];
  const subsList     = subs || [];
  const creatorsList = creators || [];

  const filteredSubs = useMemo(() => {
    const cutoff = filters.time === "all" ? 0 : Date.now() - parseInt(filters.time, 10) * 86400 * 1000;
    return subsList.filter(s => {
      if (filters.project !== "all" && s.project_id !== filters.project) return false;
      if (cutoff > 0 && s.created_at && new Date(s.created_at).getTime() < cutoff) return false;
      return true;
    });
  }, [subsList, filters]);

  const totals = useMemo(() => {
    const ps = filters.project === "all" ? projectsList : projectsList.filter(p => p.id === filters.project);
    const totalTasks = ps.reduce((s, p) => s + (p.task_count || 0), 0);
    // When filtered: use that project's approved-creator count from the project record itself
    // When unfiltered: total unique approved creators across all brand projects
    const totalCreators = filters.project === "all"
      ? creatorsList.length
      : (ps[0]?.ambassador_count || 0);
    // Assignment-based metrics: each (approved creator × task) pair = 1 assignment
    const totalAssignments = ps.reduce((s, p) => s + (p.task_count || 0) * (p.ambassador_count || 0), 0);
    const scopedSubs = filters.project === "all"
      ? subsList
      : subsList.filter(s => s.project_id === filters.project);
    const completedAssignments = scopedSubs.filter(s => s.status === "Approved").length;
    const rejectedAssignments  = scopedSubs.filter(s => s.status === "Rejected").length;
    // Pending bucket = Pending submissions + Not Started (assignments without a submission)
    const pendingAssignments  = Math.max(0, totalAssignments - completedAssignments - rejectedAssignments);
    const completionPct = totalAssignments > 0
      ? Math.round((completedAssignments / totalAssignments) * 100)
      : 0;
    return {
      ambassadors: totalCreators,
      projects: ps.length,
      // "Total Tasks" represents assigned tasks (1 per creator-task pair) — Completed + Pending
      tasks: totalAssignments,
      assignments: totalAssignments,
      completed: completedAssignments,
      pending: pendingAssignments,
      completionPct,
    };
  }, [projectsList, creatorsList, subsList, filters.project]);

  const pieData = useMemo(() => [
    { name: "Approved", value: filteredSubs.filter(s => s.status === "Approved").length },
    { name: "Pending", value: filteredSubs.filter(s => s.status === "Pending").length },
    { name: "Rejected", value: filteredSubs.filter(s => s.status === "Rejected").length },
  ].filter(d => d.value > 0), [filteredSubs]);

  // Creator demographics — scoped only by the top-level project filter
  const filteredCreators = useMemo(() => {
    if (filters.project === "all") return creatorsList;
    const ids = new Set(subsList.filter(s => s.project_id === filters.project).map(s => s.ambassador_id));
    return creatorsList.filter(c => ids.has(c.id));
  }, [creatorsList, subsList, filters.project]);

  // City distribution — top 5 only
  const cityData = useMemo(() => {
    const counts = {};
    filteredCreators.forEach(c => { const k = c.city || "Unknown"; counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).sort((a,b) => b[1] - a[1]).slice(0, 5).map(([name, value]) => ({ name, value }));
  }, [filteredCreators]);

  // Gender distribution — only Male / Female / Prefer not to say (no "Other", no "Unknown" bucket leakage)
  const genderData = useMemo(() => {
    const counts = { Male: 0, Female: 0, "Prefer not to say": 0 };
    filteredCreators.forEach(c => {
      const g = (c.gender || "").trim();
      if (GENDER_BUCKETS.includes(g)) counts[g] += 1;
      else counts["Prefer not to say"] += 1;
    });
    return GENDER_BUCKETS.map(k => ({ name: k, value: counts[k] })).filter(d => d.value > 0);
  }, [filteredCreators]);

  const selectedProject = filters.project !== "all" ? projectsList.find(p => p.id === filters.project) : null;

  return (
    <div className="space-y-6">
      <SectionTitle title={user?.company || user?.name || "Brand HQ"} subtitle="Manage your campus campaigns." />

      {/* Filters */}
      <div className="glass p-3 flex flex-wrap gap-2 items-center" data-testid="brand-dash-filters">
        <div className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted tracking-wider"><Funnel size={14} weight="bold" /> Filter</div>
        <select value={filters.project} onChange={e => setFilters({...filters, project:e.target.value})} className="input-base text-sm py-1.5 max-w-[220px]" data-testid="brand-filter-project">
          <option value="all">All projects</option>
          {projectsList.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select value={filters.time} onChange={e => setFilters({...filters, time:e.target.value})} className="input-base text-sm py-1.5 max-w-[180px]" data-testid="brand-filter-time">
          {TIME_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Project Summary — 6 compact cards in one row */}
      <div>
        <div className="text-white font-bold text-lg mb-3">Project Summary</div>
        {projects === null || creators === null ? <StatsSkeleton /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="brand-summary-cards">
            <StatCard label="Total Ambassadors" value={totals.ambassadors} icon={Users} accent="pink" />
            <StatCard label="Total Projects" value={totals.projects} icon={Stack} accent="cyan" />
            <StatCard label="Total Tasks" value={totals.tasks} icon={ListChecks} accent="amber" />
            <StatCard label="Pending Tasks" value={fmtNum(totals.pending)} icon={ChartBar} accent="amber" />
            <StatCard label="Completed Tasks" value={fmtNum(totals.completed)} icon={ChartBar} accent="lime" />
            <StatCard label="Completion %" value={`${totals.completionPct}%`} icon={ChartBar} accent="lime" />
          </div>
        )}

        {/* View button — All Projects, or selected project shortcut */}
        <div className="mt-4">
          {selectedProject
            ? <Link to={`/client/projects/${selectedProject.id}`} className="btn-primary inline-flex items-center gap-2" data-testid="open-selected-project">
                Open {selectedProject.title} <ArrowRight size={14} weight="bold" />
              </Link>
            : <Link to="/client/projects" className="btn-primary inline-flex items-center gap-2" data-testid="view-all-projects">
                View All Projects <ArrowRight size={14} weight="bold" />
              </Link>}
        </div>
      </div>

      {/* Three aligned charts: Gender · City · Submission Status */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="glass p-5 flex flex-col">
          <div className="text-white font-bold mb-1">Demographics · Gender</div>
          <div className="text-xs text-muted mb-3">{filteredCreators.length} creators</div>
          {creators === null ? (
            <div className="h-[260px] rounded-xl bg-white/5 animate-pulse" />
          ) : filteredCreators.length === 0 ? (
            <EmptyState title="No creators yet" />
          ) : genderData.length > 0 ? (
            <CategoryPie data={genderData} />
          ) : (
            <div className="text-xs text-muted italic">No gender data</div>
          )}
        </div>
        <div className="glass p-5 flex flex-col">
          <div className="text-white font-bold mb-1">Demographics · Top Cities</div>
          <div className="text-xs text-muted mb-3">Top 5 by creator count</div>
          {creators === null ? (
            <div className="h-[260px] rounded-xl bg-white/5 animate-pulse" />
          ) : filteredCreators.length === 0 ? (
            <EmptyState title="No creators yet" />
          ) : (
            <DemoBars rows={cityData} total={filteredCreators.length} />
          )}
        </div>
        <div className="glass p-5 flex flex-col">
          <div className="text-white font-bold mb-1">Submission Status</div>
          <div className="text-xs text-muted mb-3">{filters.project === "all" ? "All campaigns" : (selectedProject?.title || "")}</div>
          {subs === null
            ? <div className="h-[260px] rounded-xl bg-white/5 animate-pulse" />
            : pieData.length > 0
              ? <CategoryPie data={pieData} />
              : <EmptyState title="No submissions yet" />}
        </div>
      </div>
    </div>
  );
}

function DemoBars({ rows, total }) {
  if (rows.length === 0) return <div className="text-xs text-muted italic">No data</div>;
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const pct = Math.round((r.value / max) * 100);
        const sharePct = Math.round((r.value / Math.max(1, total)) * 100);
        return (
          <div key={r.name} data-testid={`demo-bar-${r.name}`}>
            <div className="flex justify-between text-[11px] mb-1">
              <span className="text-white truncate">{r.name}</span>
              <span className="text-muted">{r.value} · {sharePct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full" style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[i % COLORS.length]}99)`,
                boxShadow: `0 0 10px ${COLORS[i % COLORS.length]}88`,
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
